'use client';

import * as React from 'react';

import { Button } from '@/hen-ui/atomics/Button';
import { BUTTON_ICON_STROKE_CLASS } from '@/hen-ui/atomics/Button/button.variants';
import { Icon, type IconName } from '@/hen-ui/atomics/Icon';
import { cn } from '@/hen-ui/utils';

import { usePopoverButtonAttached } from './popover-button-context';
import { usePopoverInsideNavbarBar } from './popover-navbar-context';
import {
  POPOVER_BUTTON_ICON_CLOSED_CLASS,
  POPOVER_BUTTON_ICON_OPEN_CLASS,
  POPOVER_BUTTON_LABEL_HIDDEN_BELOW_CLASS,
  popoverButtonBarTopClass,
  popoverButtonVariants,
} from './popover-button.variants';
import type { PopoverCollapseBelow } from './popover.variants';

export interface PopoverButtonProps
  extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'children' | 'rel'
  > {
  /** The visible label, and the accessible name when `labelHidden` is set. */
  children: React.ReactNode;
  /** Leading icon. */
  icon?: IconName;
  /** Leading icon while the popover is open. Without it `icon` stays. */
  expandedIcon?: IconName;
  /** Shows only the icon; the label stays as the accessible name. Needs `icon`. */
  labelHidden?: boolean;
  /**
   * Shows only the icon below this screen size and the label with the icon
   * from it up; the label stays the accessible name. Needs `icon`;
   * `labelHidden` wins.
   */
  labelHiddenBelow?: PopoverCollapseBelow;
}

function hasIcon(icon: IconName | undefined): icon is IconName {
  return typeof icon === 'string' && icon.trim() !== '';
}

/**
 * The trigger of a Popover: a padded ghost Button with an optional leading
 * icon, or a round icon button when the label is hidden, at every width or
 * below `labelHiddenBelow`. Render it inside `Popover.Trigger`; it follows
 * the popover's open state through `aria-expanded`.
 */
export const PopoverButton = React.forwardRef<
  HTMLButtonElement,
  PopoverButtonProps
>(
  (
    {
      children,
      icon,
      expandedIcon,
      labelHidden = false,
      labelHiddenBelow,
      className,
      type = 'button',
      ...props
    },
    ref,
  ) => {
    const attached = usePopoverButtonAttached();
    const insideNavbarBar = usePopoverInsideNavbarBar();
    const showIcon = hasIcon(icon);
    const openIcon =
      showIcon && hasIcon(expandedIcon) ? expandedIcon : undefined;
    const hideLabel = labelHidden && showIcon;
    const hideLabelBelow =
      showIcon && !hideLabel && labelHiddenBelow ? labelHiddenBelow : undefined;
    // Pinned to the bar top whenever a label can show (including from
    // labelHiddenBelow up). Icon-only stays centered.
    const pinsToBarTop = attached && insideNavbarBar && !hideLabel;

    return (
      <Button
        ref={ref}
        type={type}
        variant='ghost'
        size={hideLabel ? 'icon' : 'lg'}
        className={cn(
          popoverButtonVariants({
            attached,
            iconOnly: hideLabel,
            labelHiddenBelow: hideLabelBelow,
          }),
          popoverButtonBarTopClass(pinsToBarTop, hideLabelBelow),
          className,
        )}
        {...props}
      >
        {showIcon ? (
          <Icon
            icon={icon}
            aria-hidden={true}
            className={cn(
              BUTTON_ICON_STROKE_CLASS,
              openIcon && POPOVER_BUTTON_ICON_CLOSED_CLASS,
            )}
          />
        ) : null}
        {openIcon ? (
          <Icon
            icon={openIcon}
            aria-hidden={true}
            className={cn(
              BUTTON_ICON_STROKE_CLASS,
              POPOVER_BUTTON_ICON_OPEN_CLASS,
            )}
          />
        ) : null}
        <span
          className={
            hideLabel
              ? 'sr-only'
              : hideLabelBelow &&
                POPOVER_BUTTON_LABEL_HIDDEN_BELOW_CLASS[hideLabelBelow]
          }
        >
          {children}
        </span>
      </Button>
    );
  },
);

PopoverButton.displayName = 'PopoverButton';
